import React, { useMemo, useState } from "react";
import "./ResumeMaker.css";

const createEducationItem = () => ({
  institution: "",
  degree: "",
  cgpa: "",
  start: "",
  end: "",
  details: "",
});

const createExperienceItem = () => ({
  company: "",
  role: "",
  start: "",
  end: "",
  details: "",
});

const createProjectItem = () => ({
  name: "",
  techStacks: "",
  liveLink: "",
  githubLink: "",
  details: "",
});

const createAchievementItem = () => ({
  title: "",
});

const DEFAULT_DATA = {
  fullName: "",
  headline: "",
  email: "",
  phone: "",
  address: "",
  portfolio: "",
  linkedin: "",
  github: "",
  summary: "",
  skills: "",
  languages: "",
  certifications: "",
  photo: "",
  education: [createEducationItem()],
  experience: [createExperienceItem()],
  projects: [createProjectItem()],
  achievements: [createAchievementItem()],
};

const DEFAULT_SECTION_VISIBILITY = {
  summary: true,
  skills: true,
  experience: true,
  education: true,
  projects: true,
  certifications: true,
  languages: true,
  achievements: true,
};

const FONT_OPTIONS = [
  { id: "poppins", label: "Poppins", stack: "'Poppins', 'Segoe UI', Arial, sans-serif" },
  { id: "source", label: "Source Sans 3", stack: "'Source Sans 3', 'Segoe UI', Arial, sans-serif" },
  { id: "lato", label: "Lato", stack: "Lato, 'Segoe UI', Arial, sans-serif" },
  { id: "georgia", label: "Georgia", stack: "Georgia, 'Times New Roman', serif" },
  { id: "ibm", label: "IBM Plex Sans", stack: "'IBM Plex Sans', 'Segoe UI', Arial, sans-serif" },
];

const TEMPLATES = [
  { id: "modern", label: "Template 1" },
  { id: "classic", label: "Template 2" },
  { id: "sidebar", label: "Template 3" },
  { id: "minimal", label: "Template 4" },
  { id: "executive", label: "Template 5" },
  { id: "compact", label: "Template 6" },
  { id: "mono", label: "Template 7" },
  { id: "builder-one", label: "Template 8" },
  { id: "builder-two", label: "Template 9" },
];

const escapeHtml = (value = "") =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const splitByComma = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeUrl = (value = "") => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const renderLinkLine = (label, url, icon) => {
  const safeUrl = escapeHtml(normalizeUrl(url));
  if (!safeUrl) return "";
  return `<span class="link-item"><a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${icon} ${escapeHtml(label)}</a></span>`;
};

const buildResumeHtml = (data, selectedTemplate, selectedFont, sectionVisibility) => {
  const fontStack = FONT_OPTIONS.find((font) => font.id === selectedFont)?.stack || FONT_OPTIONS[0].stack;
  const safe = {
    ...data,
    fullName: escapeHtml(data.fullName || "Your Name"),
    headline: escapeHtml(data.headline),
    email: escapeHtml(data.email),
    phone: escapeHtml(data.phone),
    address: escapeHtml(data.address),
    portfolio: escapeHtml(data.portfolio),
    linkedin: escapeHtml(data.linkedin),
    github: escapeHtml(data.github),
    summary: escapeHtml(data.summary),
    skills: splitByComma(data.skills).map(escapeHtml),
    languages: splitByComma(data.languages).map(escapeHtml),
    certifications: splitByComma(data.certifications).map(escapeHtml),
  };

  const contactItems = [
    safe.email ? `<span>✉ ${safe.email}</span>` : "",
    safe.phone ? `<span>☎ ${safe.phone}</span>` : "",
    safe.address ? `<span>⌂ ${safe.address}</span>` : "",
  ].filter(Boolean);

  const profileLinks = [
    renderLinkLine("Portfolio", data.portfolio, "🌐"),
    renderLinkLine("LinkedIn", data.linkedin, "in"),
    renderLinkLine("GitHub", data.github, "<>"),
  ]
    .filter(Boolean)
    .join("");

  const sectionItems = {
    education: data.education
      .filter((item) => item.institution || item.degree || item.cgpa || item.details || item.start || item.end)
      .map(
        (item) => `
        <div class="item">
          <div class="row"><strong>${escapeHtml(item.degree || "Education")}</strong><span>${escapeHtml(
            [item.start, item.end].filter(Boolean).join(" - ")
          )}</span></div>
          <div class="sub">${escapeHtml(item.institution)}</div>
          ${item.cgpa ? `<div class="sub"><strong>CGPA:</strong> ${escapeHtml(item.cgpa)}</div>` : ""}
          ${item.details ? `<p>${escapeHtml(item.details)}</p>` : ""}
        </div>
      `
      )
      .join(""),
    experience: data.experience
      .filter((item) => item.company || item.role || item.details || item.start || item.end)
      .map(
        (item) => `
        <div class="item">
          <div class="row"><strong>${escapeHtml(item.role || "Experience")}</strong><span>${escapeHtml(
            [item.start, item.end].filter(Boolean).join(" - ")
          )}</span></div>
          <div class="sub">${escapeHtml(item.company)}</div>
          ${item.details ? `<p>${escapeHtml(item.details)}</p>` : ""}
        </div>
      `
      )
      .join(""),
    projects: data.projects
      .filter((item) => item.name || item.details || item.techStacks || item.liveLink || item.githubLink)
      .map(
        (item) => `
        <div class="item">
          <div class="row"><strong>${escapeHtml(item.name || "Project")}</strong></div>
          ${
            item.techStacks
              ? `<div class="chips">${splitByComma(item.techStacks)
                  .map((stack) => `<span class="chip">${escapeHtml(stack)}</span>`)
                  .join("")}</div>`
              : ""
          }
          ${
            item.liveLink || item.githubLink
              ? `<div class="links">
                  ${
                    item.liveLink
                      ? `<span class="link-item"><a href="${escapeHtml(normalizeUrl(item.liveLink))}" target="_blank" rel="noopener noreferrer">Live Link</a></span>`
                      : ""
                  }
                  ${
                    item.githubLink
                      ? `<span class="link-item"><a href="${escapeHtml(normalizeUrl(item.githubLink))}" target="_blank" rel="noopener noreferrer">GitHub</a></span>`
                      : ""
                  }
                </div>`
              : ""
          }
          ${item.details ? `<p>${escapeHtml(item.details)}</p>` : ""}
        </div>
      `
      )
      .join(""),
    achievements: data.achievements
      .filter((item) => item.title)
      .map((item) => `<li>${escapeHtml(item.title)}</li>`)
      .join(""),
  };

  const has = {
    summary: Boolean(safe.summary),
    skills: safe.skills.length > 0,
    experience: Boolean(sectionItems.experience),
    education: Boolean(sectionItems.education),
    projects: Boolean(sectionItems.projects),
    certifications: safe.certifications.length > 0,
    languages: safe.languages.length > 0,
    achievements: Boolean(sectionItems.achievements),
  };

  const canShow = (sectionName) => sectionVisibility[sectionName] && has[sectionName];
  const photoBlock = data.photo ? `<img src="${data.photo}" alt="Profile" class="photo" />` : "";

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${safe.fullName} - Resume</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: ${fontStack};
      background: #f2f4f7;
      padding: 24px;
      color: #101828;
      line-height: 1.45;
    }
    .resume { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; border-radius: 12px; overflow: hidden; }
    .header { display: flex; gap: 16px; align-items: center; padding: 24px; background: #0f6c49; color: #fff; }
    .photo { width: 92px; height: 92px; object-fit: cover; border-radius: 12px; border: 2px solid rgba(255,255,255,.45); }
    .name { font-size: 30px; margin: 0; }
    .headline { margin: 4px 0 8px; font-size: 16px; opacity: .95; }
    .contact { font-size: 13px; opacity: .95; display: flex; flex-wrap: wrap; gap: 6px 14px; margin-bottom: 8px; }
    .links { font-size: 12px; display: flex; flex-wrap: wrap; gap: 6px 14px; }
    .link-item a { color: inherit; text-decoration: underline; word-break: break-all; }
    .content { padding: 24px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    h2 { margin: 0 0 10px; font-size: 16px; border-bottom: 1px solid #e4e7ec; padding-bottom: 6px; }
    .item { margin-bottom: 12px; }
    .row { display: flex; justify-content: space-between; gap: 8px; font-size: 14px; }
    .sub { color: #344054; font-size: 13px; margin: 3px 0; }
    p, li { margin: 4px 0; color: #1d2939; font-size: 13px; }
    ul { margin: 0; padding-left: 18px; }
    .chips { display: flex; flex-wrap: wrap; gap: 8px; }
    .chip { font-size: 12px; background: #ecfdf3; color: #027a48; padding: 4px 10px; border-radius: 999px; }

    .tpl-classic .header { background: #101828; border-bottom: 4px solid #344054; }
    .tpl-classic .chip { background: #f2f4f7; color: #344054; }

    .tpl-sidebar { display: grid; grid-template-columns: 290px 1fr; }
    .tpl-sidebar .left { background: #0b1320; color: #fff; padding: 24px; }
    .tpl-sidebar .left h2 { border-color: rgba(255,255,255,.22); }
    .tpl-sidebar .left p, .tpl-sidebar .left li, .tpl-sidebar .left .sub { color: #e4e7ec; }
    .tpl-sidebar .left .link-item a { color: #e4e7ec; }
    .tpl-sidebar .right { padding: 24px; }
    .tpl-sidebar .header { background: transparent; padding: 0 0 14px; color: inherit; }
    .tpl-sidebar .contact { margin-bottom: 12px; }

    .tpl-minimal .header { background: #fff; color: #101828; border-bottom: 1px solid #eaecf0; }
    .tpl-minimal .contact { color: #475467; }
    .tpl-minimal .chip { background: #f2f4f7; color: #344054; }

    .tpl-executive .header { background: linear-gradient(90deg, #111827 0%, #1f2937 100%); }
    .tpl-executive h2 { border-bottom: 2px solid #111827; }
    .tpl-executive .chip { background: #111827; color: #fff; }

    .tpl-compact .header { padding: 16px 24px; }
    .tpl-compact .name { font-size: 24px; }
    .tpl-compact .content { padding: 16px 24px; }
    .tpl-compact .item { margin-bottom: 8px; }
    .tpl-compact p, .tpl-compact li { font-size: 12px; }

    .tpl-mono .header { background: #2f2f2f; }
    .tpl-mono .chip { background: #f5f5f5; color: #1f2937; border: 1px solid #d0d5dd; }

    .tpl-builder-one { border-radius: 0; padding: 8mm 8mm 9mm; }
    .tpl-builder-one .header {
      background: #fff;
      color: #101828;
      border-bottom: 0;
      justify-content: center;
      text-align: center;
      flex-direction: column;
      gap: 8px;
      padding: 6px 0 2px;
    }
    .tpl-builder-one .contact, .tpl-builder-one .links {
      justify-content: center;
      width: 100%;
      color: #1d2939;
    }
    .tpl-builder-one .content { padding: 6px 0 0; }
    .tpl-builder-one h2 {
      text-transform: uppercase;
      text-align: center;
      font-size: 14px;
      letter-spacing: .08em;
      border-bottom: 1px solid #1f2937;
    }

    .tpl-builder-two { border-radius: 0; padding: 8mm 8mm 9mm; }
    .tpl-builder-two .header {
      background: #fff;
      color: #101828;
      border-bottom: 1px solid #d0d5dd;
      padding: 6px 0 10px;
    }
    .tpl-builder-two .header > div {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
      gap: 4px;
    }
    .tpl-builder-two .content { padding: 12px 0 0; }
    .tpl-builder-two h2 {
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: .06em;
    }

    @media (max-width: 760px) {
      .tpl-sidebar { grid-template-columns: 1fr; }
      .grid { grid-template-columns: 1fr; }
      .resume { width: 100%; min-height: auto; }
    }

    @media print {
      html { border: none; outline: none; }
      body { background: #fff; padding: 0; margin: 0; border: none; outline: none; }
      .resume {
        width: 210mm;
        min-height: 297mm;
        border-radius: 0;
        margin: 0;
        box-shadow: none;
      }
      a { text-decoration: none; }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
    @page {
      size: A4;
      margin: 0mm;
    }
  </style>
</head>
<body>
  <article class="resume tpl-${selectedTemplate}" id="resume-root">
    ${
      selectedTemplate === "sidebar"
        ? `<section class="left">
             <div class="header">${photoBlock}<div><h1 class="name">${safe.fullName}</h1>${safe.headline ? `<div class="headline">${safe.headline}</div>` : ""}</div></div>
             <h2>Contact</h2>
             <p>${contactItems.join("<br />")}</p>
             ${profileLinks ? `<div class="links">${profileLinks}</div>` : ""}
             ${canShow("skills") ? `<h2>Skills</h2><div class="chips">${safe.skills.map((s) => `<span class="chip">${s}</span>`).join("")}</div>` : ""}
             ${canShow("languages") ? `<h2>Languages</h2><ul>${safe.languages.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
             ${canShow("certifications") ? `<h2>Certifications</h2><ul>${safe.certifications.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
           </section>
           <section class="right">
             ${canShow("summary") ? `<h2>Professional Summary</h2><p>${safe.summary}</p>` : ""}
             ${canShow("experience") ? `<h2>Experience</h2>${sectionItems.experience}` : ""}
             ${canShow("education") ? `<h2>Education</h2>${sectionItems.education}` : ""}
             ${canShow("projects") ? `<h2>Projects</h2>${sectionItems.projects}` : ""}
             ${canShow("achievements") ? `<h2>Achievements</h2><ul>${sectionItems.achievements}</ul>` : ""}
           </section>`
        : selectedTemplate === "builder-one"
        ? `<header class="header">${photoBlock}<div><h1 class="name">${safe.fullName}</h1>${safe.headline ? `<div class="headline">${safe.headline}</div>` : ""}<div class="contact">${contactItems
            .map((item) => `<span>${item}</span>`)
            .join("")}</div>${profileLinks ? `<div class="links">${profileLinks}</div>` : ""}</div></header>
           <section class="content">
             ${canShow("summary") ? `<h2>Professional Summary</h2><p>${safe.summary}</p>` : ""}
             ${canShow("skills") ? `<h2>Skills</h2><div class="chips">${safe.skills.map((s) => `<span class="chip">${s}</span>`).join("")}</div>` : ""}
             ${canShow("experience") ? `<h2>Experience</h2>${sectionItems.experience}` : ""}
             ${canShow("education") ? `<h2>Education</h2>${sectionItems.education}` : ""}
             ${canShow("projects") ? `<h2>Projects</h2>${sectionItems.projects}` : ""}
             ${canShow("certifications") ? `<h2>Certifications</h2><ul>${safe.certifications.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
             ${canShow("languages") ? `<h2>Languages</h2><ul>${safe.languages.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
             ${canShow("achievements") ? `<h2>Achievements</h2><ul>${sectionItems.achievements}</ul>` : ""}
           </section>`
        : selectedTemplate === "builder-two"
        ? `<header class="header">${photoBlock}<div><h1 class="name">${safe.fullName}</h1>${safe.headline ? `<div class="headline">${safe.headline}</div>` : ""}<div class="contact">${contactItems
            .map((item) => `<span>${item}</span>`)
            .join("")}</div>${profileLinks ? `<div class="links">${profileLinks}</div>` : ""}</div></header>
           <section class="content">
             ${canShow("summary") ? `<h2>Professional Summary</h2><p>${safe.summary}</p>` : ""}
             <div class="grid">
               <div>
                 ${canShow("experience") ? `<h2>Experience</h2>${sectionItems.experience}` : ""}
                 ${canShow("projects") ? `<h2>Projects</h2>${sectionItems.projects}` : ""}
               </div>
               <div>
                 ${canShow("education") ? `<h2>Education</h2>${sectionItems.education}` : ""}
                 ${canShow("skills") ? `<h2>Skills</h2><div class="chips">${safe.skills.map((s) => `<span class="chip">${s}</span>`).join("")}</div>` : ""}
                 ${canShow("certifications") ? `<h2>Certifications</h2><ul>${safe.certifications.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
                 ${canShow("languages") ? `<h2>Languages</h2><ul>${safe.languages.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
                 ${canShow("achievements") ? `<h2>Achievements</h2><ul>${sectionItems.achievements}</ul>` : ""}
               </div>
             </div>
           </section>`
        : `<header class="header">${photoBlock}<div><h1 class="name">${safe.fullName}</h1>${safe.headline ? `<div class="headline">${safe.headline}</div>` : ""}<div class="contact">${contactItems
            .map((item) => `<span>${item}</span>`)
            .join("")}</div>${profileLinks ? `<div class="links">${profileLinks}</div>` : ""}</div></header>
           <section class="content">
             ${canShow("summary") ? `<h2>Professional Summary</h2><p>${safe.summary}</p>` : ""}
             ${canShow("skills") ? `<h2>Skills</h2><div class="chips">${safe.skills.map((s) => `<span class="chip">${s}</span>`).join("")}</div>` : ""}
             <div class="grid">
               <div>
                 ${canShow("experience") ? `<h2>Experience</h2>${sectionItems.experience}` : ""}
                 ${canShow("projects") ? `<h2>Projects</h2>${sectionItems.projects}` : ""}
               </div>
               <div>
                 ${canShow("education") ? `<h2>Education</h2>${sectionItems.education}` : ""}
                 ${canShow("certifications") ? `<h2>Certifications</h2><ul>${safe.certifications.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
                 ${canShow("languages") ? `<h2>Languages</h2><ul>${safe.languages.map((s) => `<li>${s}</li>`).join("")}</ul>` : ""}
                 ${canShow("achievements") ? `<h2>Achievements</h2><ul>${sectionItems.achievements}</ul>` : ""}
               </div>
             </div>
           </section>`
    }
  </article>
</body>
</html>
`;
};

const ResumeMaker = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATES[0].id);
  const [selectedFont, setSelectedFont] = useState(FONT_OPTIONS[0].id);
  const [sectionVisibility, setSectionVisibility] = useState(DEFAULT_SECTION_VISIBILITY);
  const [data, setData] = useState(DEFAULT_DATA);

  const updateField = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSection = (sectionName) => {
    setSectionVisibility((prev) => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const updateListField = (listKey, index, key, value) => {
    setData((prev) => {
      const nextList = [...prev[listKey]];
      nextList[index] = { ...nextList[index], [key]: value };
      return { ...prev, [listKey]: nextList };
    });
  };

  const addListItem = (listKey, createItem) => {
    setData((prev) => ({ ...prev, [listKey]: [...prev[listKey], createItem()] }));
  };

  const removeListItem = (listKey, index) => {
    setData((prev) => {
      const nextList = prev[listKey].filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, [listKey]: nextList };
    });
  };

  const onPhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      updateField("photo", "");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      updateField("photo", String(reader.result || ""));
    };
    reader.readAsDataURL(file);
  };

  const resumeHtml = useMemo(
    () => buildResumeHtml(data, selectedTemplate, selectedFont, sectionVisibility),
    [data, sectionVisibility, selectedTemplate, selectedFont]
  );

  const handleDownloadPdf = () => {
    try {
      const printWindow = window.open("", "_blank", "width=1280,height=900");
      if (!printWindow) {
        window.alert("Please allow popups to export PDF.");
        return;
      }

      const autoPrintScript = [
        "<script>",
        "(function () {",
        "  function triggerPrint() {",
        "    try {",
        "      window.focus();",
        "      setTimeout(function () { window.print(); }, 200);",
        "    } catch (e) {}",
        "  }",
        "  if (document.readyState === 'complete') {",
        "    triggerPrint();",
        "  } else {",
        "    window.addEventListener('load', triggerPrint, { once: true });",
        "  }",
        "})();",
        "</script>",
      ].join("");

      const printableHtml = resumeHtml.includes("</body>")
        ? resumeHtml.replace("</body>", `${autoPrintScript}</body>`)
        : `${resumeHtml}${autoPrintScript}`;

      printWindow.document.open();
      printWindow.document.write(printableHtml);
      printWindow.document.close();

      // Parent fallback for browsers that ignore popup load listeners.
      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (e) {}
      }, 900);
    } catch (error) {
      console.error("PDF generation failed:", error);
      window.alert("PDF download failed. Please try again.");
    }
  };

  const renderSectionToggle = (id, label) => (
    <label className="resume-toggle" key={id}>
      <input type="checkbox" checked={sectionVisibility[id]} onChange={() => toggleSection(id)} />
      <span>{label}</span>
    </label>
  );

  const renderEmptyHint = (label, listKey, factory) => {
    if (data[listKey].length > 0) return null;
    return (
      <div className="resume-empty-row">
        <span>{label} section has no items.</span>
        <button type="button" onClick={() => addListItem(listKey, factory)}>
          Add First Item
        </button>
      </div>
    );
  };

  return (
    <main className="resume-maker-page">
      <section className="resume-maker-hero">
        <div className="resume-maker-kicker">Student Resume Builder</div>
        <h1>Create a polished CV in minutes</h1>
        <p>
          Fill in your details, choose templates and font, make photo optional, hide unused sections, and export your CV as <strong>PDF</strong>.
        </p>
      </section>

      <section className="resume-maker-layout">
        <div className="resume-form-panel">
          <div className="resume-top-controls">
            <label>
              Resume Font
              <select value={selectedFont} onChange={(e) => setSelectedFont(e.target.value)}>
                {FONT_OPTIONS.map((font) => (
                  <option key={font.id} value={font.id}>
                    {font.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="resume-toggle-wrap">
            {renderSectionToggle("summary", "Summary")}
            {renderSectionToggle("skills", "Skills")}
            {renderSectionToggle("experience", "Experience")}
            {renderSectionToggle("education", "Education")}
            {renderSectionToggle("projects", "Projects")}
            {renderSectionToggle("certifications", "Certifications")}
            {renderSectionToggle("languages", "Languages")}
            {renderSectionToggle("achievements", "Achievements")}
          </div>

          <div className="resume-form-grid">
            <label>
              Full Name
              <input value={data.fullName} onChange={(e) => updateField("fullName", e.target.value)} placeholder="Jane Doe" />
            </label>
            <label>
              Headline
              <input value={data.headline} onChange={(e) => updateField("headline", e.target.value)} placeholder="Software Engineering Student" />
            </label>
            <label>
              Email
              <input value={data.email} onChange={(e) => updateField("email", e.target.value)} placeholder="you@email.com" />
            </label>
            <label>
              Phone
              <input value={data.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder="+8801XXXXXXXXX" />
            </label>
            <label>
              Address
              <input value={data.address} onChange={(e) => updateField("address", e.target.value)} placeholder="Dhaka, Bangladesh" />
            </label>
            <label>
              Portfolio
              <input value={data.portfolio} onChange={(e) => updateField("portfolio", e.target.value)} placeholder="your-portfolio.com" />
            </label>
            <label>
              LinkedIn
              <input value={data.linkedin} onChange={(e) => updateField("linkedin", e.target.value)} placeholder="linkedin.com/in/username" />
            </label>
            <label>
              GitHub
              <input value={data.github} onChange={(e) => updateField("github", e.target.value)} placeholder="github.com/username" />
            </label>
          </div>

          <label>
            Profile Summary
            <textarea
              rows={4}
              value={data.summary}
              onChange={(e) => updateField("summary", e.target.value)}
              placeholder="Write a concise summary of your strengths, interests, and goals."
            />
          </label>

          <label>
            Optional Photo
            <input type="file" accept="image/*" onChange={onPhotoChange} />
          </label>

          <label>
            Skills (comma separated)
            <input value={data.skills} onChange={(e) => updateField("skills", e.target.value)} placeholder="C++, JavaScript, React, Problem Solving" />
          </label>

          <label>
            Languages (comma separated)
            <input value={data.languages} onChange={(e) => updateField("languages", e.target.value)} placeholder="Bangla, English" />
          </label>

          <label>
            Certifications (comma separated)
            <input value={data.certifications} onChange={(e) => updateField("certifications", e.target.value)} placeholder="AWS Cloud Practitioner, CCNA" />
          </label>

          <div className="resume-section-editor">
            <div className="resume-section-head">
              <h3>Education</h3>
              <button type="button" onClick={() => addListItem("education", createEducationItem)}>
                Add
              </button>
            </div>
            {renderEmptyHint("Education", "education", createEducationItem)}
            {data.education.map((item, index) => (
              <div className="resume-repeat-card" key={`edu-${index}`}>
                <div className="resume-form-grid">
                  <input value={item.institution} onChange={(e) => updateListField("education", index, "institution", e.target.value)} placeholder="Institution" />
                  <input value={item.degree} onChange={(e) => updateListField("education", index, "degree", e.target.value)} placeholder="Degree/Program" />
                  <input value={item.cgpa} onChange={(e) => updateListField("education", index, "cgpa", e.target.value)} placeholder="CGPA (optional)" />
                  <input value={item.start} onChange={(e) => updateListField("education", index, "start", e.target.value)} placeholder="Start (e.g. 2022)" />
                  <input value={item.end} onChange={(e) => updateListField("education", index, "end", e.target.value)} placeholder="End (e.g. Present)" />
                </div>
                <textarea
                  rows={2}
                  value={item.details}
                  onChange={(e) => updateListField("education", index, "details", e.target.value)}
                  placeholder="Relevant coursework, CGPA, honors, etc."
                />
                <button type="button" className="remove-btn" onClick={() => removeListItem("education", index)}>
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="resume-section-editor">
            <div className="resume-section-head">
              <h3>Experience</h3>
              <button type="button" onClick={() => addListItem("experience", createExperienceItem)}>
                Add
              </button>
            </div>
            {renderEmptyHint("Experience", "experience", createExperienceItem)}
            {data.experience.map((item, index) => (
              <div className="resume-repeat-card" key={`exp-${index}`}>
                <div className="resume-form-grid">
                  <input value={item.company} onChange={(e) => updateListField("experience", index, "company", e.target.value)} placeholder="Company/Organization" />
                  <input value={item.role} onChange={(e) => updateListField("experience", index, "role", e.target.value)} placeholder="Role" />
                  <input value={item.start} onChange={(e) => updateListField("experience", index, "start", e.target.value)} placeholder="Start" />
                  <input value={item.end} onChange={(e) => updateListField("experience", index, "end", e.target.value)} placeholder="End" />
                </div>
                <textarea
                  rows={2}
                  value={item.details}
                  onChange={(e) => updateListField("experience", index, "details", e.target.value)}
                  placeholder="Mention your impact, responsibilities, and achievements."
                />
                <button type="button" className="remove-btn" onClick={() => removeListItem("experience", index)}>
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="resume-section-editor">
            <div className="resume-section-head">
              <h3>Projects</h3>
              <button type="button" onClick={() => addListItem("projects", createProjectItem)}>
                Add
              </button>
            </div>
            {renderEmptyHint("Projects", "projects", createProjectItem)}
            {data.projects.map((item, index) => (
              <div className="resume-repeat-card" key={`project-${index}`}>
                <div className="resume-form-grid">
                  <input value={item.name} onChange={(e) => updateListField("projects", index, "name", e.target.value)} placeholder="Project Name" />
                  <input
                    value={item.techStacks}
                    onChange={(e) => updateListField("projects", index, "techStacks", e.target.value)}
                    placeholder="Tech Stacks (comma separated)"
                  />
                  <input value={item.liveLink} onChange={(e) => updateListField("projects", index, "liveLink", e.target.value)} placeholder="Live Link (optional)" />
                  <input value={item.githubLink} onChange={(e) => updateListField("projects", index, "githubLink", e.target.value)} placeholder="GitHub Link (optional)" />
                </div>
                <textarea
                  rows={2}
                  value={item.details}
                  onChange={(e) => updateListField("projects", index, "details", e.target.value)}
                  placeholder="Tech stack, features, and your contribution."
                />
                <button type="button" className="remove-btn" onClick={() => removeListItem("projects", index)}>
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="resume-section-editor">
            <div className="resume-section-head">
              <h3>Achievements</h3>
              <button type="button" onClick={() => addListItem("achievements", createAchievementItem)}>
                Add
              </button>
            </div>
            {renderEmptyHint("Achievements", "achievements", createAchievementItem)}
            {data.achievements.map((item, index) => (
              <div className="resume-repeat-card" key={`achievement-${index}`}>
                <input value={item.title} onChange={(e) => updateListField("achievements", index, "title", e.target.value)} placeholder="Achievement title" />
                <button type="button" className="remove-btn" onClick={() => removeListItem("achievements", index)}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="resume-preview-panel">
          <div className="preview-control-bar">
            <div className="template-buttons">
              {TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className={selectedTemplate === template.id ? "active" : ""}
                  onClick={() => setSelectedTemplate(template.id)}
                >
                  {template.label}
                </button>
              ))}
            </div>
            <div className="download-buttons">
              <button type="button" onClick={handleDownloadPdf}>
                Export PDF
              </button>
            </div>
          </div>
          <iframe title="Resume Preview" srcDoc={resumeHtml} className="resume-iframe" />
        </div>
      </section>
    </main>
  );
};

export default ResumeMaker;
